import type { PixelData } from "ag-psd";

import { buildAiDisclosureXmp } from "@hightouch/lib/ad-studio/disclaimer/xmp";
import {
  planLayers,
  type LayerPlan,
} from "src/pages/ad-studio/layered-export/plan-layers";
import {
  withPreparedLayeredExport,
  type LayeredExportArgs,
  type PreparedLayeredExport,
} from "src/pages/ad-studio/layered-export/prepare-layered-export";
import { trimTransparentPixels } from "src/pages/ad-studio/psd-export/alpha-trim";
import { parseCssColor } from "@hightouch/lib/ad-studio/psd-export/psd-color";
import { createIdmlPackage, element, xmlRoot } from "./idml-package";
import {
  createIdmlTextPreparer,
  type PreparedIdmlText,
} from "./prepare-idml-text";
export { IDML_MIME_TYPE } from "./idml-package";
const BLEND_MODES: Record<NonNullable<LayerPlan["blendMode"]>, string> = {
  normal: "Normal",
  darken: "Darken",
  multiply: "Multiply",
  "color burn": "ColorBurn",
  lighten: "Lighten",
  screen: "Screen",
  "color dodge": "ColorDodge",
  overlay: "Overlay",
  "soft light": "SoftLight",
  "hard light": "HardLight",
  difference: "Difference",
  exclusion: "Exclusion",
  hue: "Hue",
  saturation: "Saturation",
  color: "Color",
  luminosity: "Luminosity",
};

function pathGeometry(frame: Element, width: number, height: number): void {
  const array = element(
    element(
      element(element(frame, "Properties"), "PathGeometry"),
      "GeometryPathType",
      { PathOpen: false },
    ),
    "PathPointArray",
  );
  for (const [x, y] of [
    [0, 0],
    [0, height],
    [width, height],
    [width, 0],
  ]) {
    const point = `${x} ${y}`;
    element(array, "PathPointType", {
      Anchor: point,
      LeftDirection: point,
      RightDirection: point,
    });
  }
}

export function buildIdmlBlob(args: LayeredExportArgs): Promise<Blob> {
  return withPreparedLayeredExport(args, (prepared) =>
    buildIdmlDocument({
      ...prepared,
      resolveFont: args.resolveFont,
      machineReadableDisclosure: args.machineReadableDisclosure,
    }),
  );
}

/** Browser captures supply image bytes; the XML writer can also be tested with an injected encoder. */
export async function buildIdmlDocument(
  args: PreparedLayeredExport &
    Pick<LayeredExportArgs, "resolveFont" | "machineReadableDisclosure"> & {
      encodePng?: (pixels: PixelData) => Promise<string>;
    },
): Promise<Blob> {
  const { doc, rasterizeDoc } = args;
  const encodePng = args.encodePng ?? pixelsToPngBase64;
  const pkg = createIdmlPackage(doc);
  const { root, graphic, fonts, spread } = pkg;
  const colors = new Map<string, string>();
  function color(css: string): string {
    const { color: rgb } = parseCssColor(css);
    const value = `${rgb.r} ${rgb.g} ${rgb.b}`;
    const existing = colors.get(value);
    if (existing) return existing;
    const id = `Color/c${colors.size}`;
    colors.set(value, id);
    element(graphic, "Color", {
      Self: id,
      Name: value,
      Model: "Process",
      Space: "RGB",
      ColorValue: value,
    });
    return id;
  }
  const fontKeys = new Set<string>();
  const fontFamilies = new Map<string, Element>();
  let sequence = 0;
  const uid = () => `item_${++sequence}`;
  const stories: string[] = [];
  const prepareText = createIdmlTextPreparer(args);
  function item(
    parent: Element,
    tag: string,
    plan: Pick<LayerPlan, "name" | "hidden" | "opacity" | "blendMode">,
    transform: string,
  ): Element {
    const node = element(parent, tag, {
      Self: uid(),
      Name: plan.name,
      ItemLayer: "artwork",
      Visible: !plan.hidden,
      Locked: false,
      ItemTransform: transform,
    });
    element(element(node, "TransparencySetting"), "BlendingSetting", {
      Opacity: plan.opacity * 100,
      BlendMode: BLEND_MODES[plan.blendMode ?? "normal"],
    });
    return node;
  }
  async function bitmap(
    parent: Element,
    plan: Pick<LayerPlan, "name" | "hidden" | "opacity" | "blendMode">,
    pixels: PixelData,
  ): Promise<void> {
    const trimmed = trimTransparentPixels(pixels);
    if (!trimmed) return;
    const { left, top, imageData } = trimmed;
    const frame = item(
      parent,
      "Rectangle",
      plan,
      `1 0 0 1 ${left} ${top - doc.height / 2}`,
    );
    frame.setAttribute("ContentType", "GraphicType");
    frame.setAttribute("FillColor", "Swatch/None");
    frame.setAttribute("StrokeColor", "Swatch/None");
    frame.setAttribute("StrokeWeight", "0");
    pathGeometry(frame, imageData.width, imageData.height);
    const image = element(frame, "Image", {
      Self: uid(),
      ItemTransform: "1 0 0 1 0 0",
      Space: "$ID/#Links_RGB",
      ActualPpi: "72 72",
      ImageTypeName: "$ID/PNG",
      Visible: true,
    });
    const properties = element(image, "Properties");
    element(properties, "GraphicBounds", {
      Left: 0,
      Top: 0,
      Right: imageData.width,
      Bottom: imageData.height,
    });
    element(properties, "Contents").textContent = await encodePng(imageData);
    // Embedded content is authoritative; this URI is only the asset's name in the Links panel.
    element(image, "Link", {
      Self: uid(),
      LinkResourceURI: `file:/embedded/${uid()}.png`,
      LinkResourceFormat: "$ID/PNG",
      StoredState: "Embedded",
      LinkClassID: 35906,
      LinkClientID: 257,
      LinkResourceModified: false,
      LinkObjectModified: false,
      ShowInUI: true,
      CanEmbed: true,
      CanUnembed: true,
      CanPackage: true,
      ImportPolicy: "NoAutoImport",
      ExportPolicy: "NoAutoExport",
    });
  }
  function text(
    parent: Element,
    plan: Extract<LayerPlan, { kind: "leaf" }>,
    source: PreparedIdmlText,
  ): void {
    const { layer, transform, leading, height, baseline, runs } = source;
    const storyId = uid();
    stories.push(storyId);
    const frame = item(parent, "TextFrame", plan, transform.join(" "));
    for (const [key, value] of Object.entries({
      ParentStory: storyId,
      PreviousTextFrame: "n",
      NextTextFrame: "n",
      ContentType: "TextType",
      FillColor: "Swatch/None",
      StrokeColor: "Swatch/None",
      StrokeWeight: "0",
    }))
      frame.setAttribute(key, value);
    pathGeometry(frame, layer.width, height);
    const preference = element(frame, "TextFramePreference", {
      TextColumnCount: 1,
      TextColumnFixedWidth: layer.width,
      VerticalJustification: "TopAlign",
      FirstBaselineOffset: "FixedHeight",
      MinimumFirstBaselineOffset: baseline,
    });
    const inset = element(element(preference, "Properties"), "InsetSpacing", {
      type: "list",
    });
    for (let i = 0; i < 4; i++)
      element(inset, "ListItem", { type: "unit" }).textContent = "0";
    const storyPackage = xmlRoot("Story", true);
    const story = element(storyPackage, "Story", {
      Self: storyId,
      AppliedTOCStyle: "n",
      TrackChanges: false,
      AppliedNamedGrid: "n",
    });
    element(story, "StoryPreference", {
      StoryOrientation: "Horizontal",
      StoryDirection: "LeftToRightDirection",
    });
    const align = {
      left: "LeftAlign",
      center: "CenterAlign",
      right: "RightAlign",
      justify: "LeftJustified",
    };
    const paragraph = element(story, "ParagraphStyleRange", {
      AppliedParagraphStyle: "ParagraphStyle/Normal",
      Justification: align[layer.textAlign],
      Hyphenation: false,
      SpaceAfter: 0,
      Composer: "Adobe Single-line Composer",
    });
    for (const segment of runs) {
      const { family, fontStyle, postScriptName, size, tracking } = segment;
      const fontKey = `${family}\t${fontStyle}`;
      if (!fontKeys.has(fontKey)) {
        fontKeys.add(fontKey);
        let fontFamily = fontFamilies.get(family);
        if (!fontFamily) {
          fontFamily = element(fonts, "FontFamily", {
            Self: uid(),
            Name: family,
          });
          fontFamilies.set(family, fontFamily);
        }
        const font = element(fontFamily, "Font", {
          Self: uid(),
          FontFamily: family,
          Name: `${family} ${fontStyle}`,
          FontStyleName: fontStyle,
        });
        if (postScriptName) font.setAttribute("PostScriptName", postScriptName);
      }
      const run = element(paragraph, "CharacterStyleRange", {
        AppliedCharacterStyle: "CharacterStyle/Normal",
        PointSize: size,
        FontStyle: fontStyle,
        FillColor: color(segment.color),
        Underline: segment.underline,
        Ligatures: false,
        Tracking: tracking,
      });
      if (layer.extraStyles?.["text-transform"] === "uppercase")
        run.setAttribute("Capitalization", "AllCaps");
      const properties = element(run, "Properties");
      element(properties, "AppliedFont", { type: "string" }).textContent =
        family;
      element(properties, "Leading", { type: "unit" }).textContent =
        String(leading);
      segment.text.split(/\r?\n/).forEach((line, index) => {
        if (index > 0) element(run, "Br");
        element(run, "Content").textContent = line;
      });
    }
    const path = `Stories/${storyId}.xml`;
    pkg.add(path, storyPackage);
  }
  async function append(parent: Element, plan: LayerPlan): Promise<void> {
    if (plan.kind === "group") {
      const group = item(parent, "Group", plan, "1 0 0 1 0 0");
      for (const child of plan.children) await append(group, child);
    } else {
      const preparedText = plan.editableText && prepareText(plan.editableText);
      if (preparedText) text(parent, plan, preparedText);
      else await bitmap(parent, plan, await rasterizeDoc(plan.captureDoc));
    }
  }
  for (const plan of planLayers(doc)) await append(spread, plan);
  if (args.disclosure)
    await bitmap(
      spread,
      { name: "AI disclosure", hidden: false, opacity: 1 },
      args.disclosure,
    );
  root.setAttribute("StoryList", stories.join(" "));
  if (args.machineReadableDisclosure)
    element(
      element(element(root, "MetadataPacketPreference"), "Properties"),
      "Contents",
    ).textContent = buildAiDisclosureXmp();
  return pkg.toBlob();
}

async function pixelsToPngBase64(pixels: PixelData): Promise<string> {
  const canvas = document.createElement("canvas");
  canvas.width = pixels.width;
  canvas.height = pixels.height;
  const context = canvas.getContext("2d");
  if (!context)
    throw new Error("InDesign export could not create an image canvas");
  try {
    context.putImageData(
      new ImageData(
        new Uint8ClampedArray(pixels.data),
        pixels.width,
        pixels.height,
      ),
      0,
      0,
    );
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (result) =>
          result
            ? resolve(result)
            : reject(new Error("InDesign export could not encode an image")),
        "image/png",
      ),
    );
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () =>
        typeof reader.result === "string"
          ? resolve(reader.result.slice(reader.result.indexOf(",") + 1))
          : reject(new Error("InDesign export could not read an image"));
      reader.readAsDataURL(blob);
    });
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}
